import type { Centimetres, Kilograms, Percent } from '~/types/units'
import { unwrapNumber } from '~/types/units'

/**
 * Values derived from the circumference estimate (measurement-protocol.md).
 *
 *   estimated_fat_mass  = W × (BF / 100)
 *   estimated_lean_mass = W − estimated_fat_mass
 *   waist_to_height     = waist_at_navel / height     (same units)
 *
 * Fat and lean mass inherit ALL of the uncertainty of the circumference
 * estimate and must always be labelled as estimates. Waist-to-height is a
 * ratio, not a diagnosis.
 *
 * Pure: no clock, no storage, no rounding.
 */

/**
 * Waist-to-height ratio.
 *
 * Both inputs are canonical centimetres, so the ratio is unit-free. Uses waist
 * at navel specifically — the narrowest-waist measurement is a different site
 * and must not be substituted (measurement-protocol.md).
 */
export function waistToHeightRatio(
  waistAtNavelCm: Centimetres,
  heightCm: Centimetres,
): number | undefined {
  const waist = unwrapNumber(waistAtNavelCm)
  const height = unwrapNumber(heightCm)
  if (!Number.isFinite(waist) || !Number.isFinite(height) || height <= 0 || waist <= 0) {
    return undefined
  }
  return waist / height
}

export interface MassEstimates {
  readonly fatMassKg: Kilograms
  readonly leanMassKg: Kilograms
}

/**
 * Estimated fat and lean mass.
 *
 * Returns undefined unless the body-fat percentage is a possible proportion,
 * because a negative or above-100% value would produce a negative mass — a
 * number that looks authoritative and is meaningless. The caller shows the
 * data-quality state instead.
 */
export function estimateMasses(
  weightKg: Kilograms,
  bodyFatPercent: Percent,
): MassEstimates | undefined {
  const weight = unwrapNumber(weightKg)
  const percent = unwrapNumber(bodyFatPercent)

  if (!Number.isFinite(weight) || weight <= 0) return undefined
  if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) return undefined

  const fatMass = weight * (percent / 100)
  return {
    fatMassKg: fatMass as Kilograms,
    leanMassKg: (weight - fatMass) as Kilograms,
  }
}
